// Client-side image compression, run just before upload. Re-encodes raster images
// to WebP (near-lossless quality, big weight savings) and downscales only images
// that are absurdly large for the web. Encoding happens in the browser (canvas),
// so there's no server CPU cost. Any failure falls back to the untouched original.
/**
 * Downscale when the longest edge exceeds this.
 *
 * Raised back to 2000 now that `srcset` exists. Before it, this single file had
 * to serve a phone and a full-bleed hero at once, so it was pushed down to the
 * smallest size the largest use could tolerate. With variants covering the
 * small end, the master only ever reaches wide screens — and there it is the
 * one place where extra pixels are actually seen.
 */
export const MAX_EDGE = 2000;
/**
 * Widths generated alongside the main file, for `srcset`.
 *
 * 400 covers a phone, 800 a tablet or a half-width column, 1280 the container
 * at 1×. Anything wider than the source is skipped — upscaling adds bytes and
 * no detail.
 */
export const IMAGE_VARIANT_WIDTHS = [400, 800, 1280];
/**
 * La calidad sube con el tamaño de la copia. Una de 400 px se ve en un móvil,
 * donde cada byte cuenta y la pérdida no se nota; la de 2000 px es la que se
 * mira en una pantalla grande a sangre completa, y ahí 0.80 ya enseña
 * bandas en los degradados y textura lavada en la piel o el cielo. Como las
 * copias estrechas cubren a los móviles, al máster le sobra margen para pesar
 * un poco más. 0.72 era el borde donde empezaban las bandas; nunca bajar de 0.80.
 * El JPEG de respaldo va siempre unos puntos arriba: JPEG necesita un número
 * mayor para la misma calidad percibida.
 */
const WEBP_QUALITY_BY_EDGE = [
    [1600, 0.88],
    [1000, 0.84],
    [0, 0.80],
];
const JPEG_QUALITY_BONUS = 0.05;
/**
 * Un PNG casi nunca es una foto: es un logo, una captura, un gráfico con texto.
 * Ahí el WebP con pérdida al 0.80 deja halos alrededor de las letras y los
 * bordes duros. Más calidad los quita y sigue pesando una fracción del PNG.
 */
const WEBP_QUALITY_GRAPHIC = 0.90;
const WEBP_QUALITY_GRAPHIC_LARGE = 0.92;
/** Calidad WebP para una copia cuyo lado mayor mide `longestEdge` píxeles. */
function qualityFor(sourceType, longestEdge) {
    if (sourceType === 'image/png')
        return longestEdge >= 1600 ? WEBP_QUALITY_GRAPHIC_LARGE : WEBP_QUALITY_GRAPHIC;
    return WEBP_QUALITY_BY_EDGE.find(([minEdge]) => longestEdge >= minEdge)[1];
}
// Only these are worth re-encoding. SVG is vector (no benefit); GIF may be animated
// and a canvas would flatten it to one frame — both pass through untouched.
const COMPRESSIBLE = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/bmp']);
const EXT_FOR = { 'image/webp': 'webp', 'image/jpeg': 'jpg', 'image/png': 'png' };
/**
 * Encodes the canvas as `type` — but only returns a blob when the browser
 * ACTUALLY honored the format. When a browser can't encode `type` (e.g. Safari
 * with WebP on older versions), `toBlob` silently falls back to PNG; returning
 * null there lets the caller try another format instead of shipping a mislabeled,
 * heavier file.
 */
function encode(canvas, type, quality) {
    return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob && blob.type === type ? blob : null), type, quality));
}
/**
 * WebP via a WASM encoder (libwebp) — the fallback for browsers whose canvas can't
 * encode WebP (Safari). Loaded lazily so it never ships to browsers that don't need
 * it. Keeps transparency, unlike the JPEG path.
 */
async function encodeWebpWasm(ctx, width, height, quality) {
    try {
        const imageData = ctx.getImageData(0, 0, width, height);
        const { default: encodeWebp } = await import('@jsquash/webp/encode');
        const buffer = await encodeWebp(imageData, { quality: Math.round(quality * 100) });
        return new Blob([buffer], { type: 'image/webp' });
    }
    catch (error) {
        // Loud, because this is the only WebP path Safari has: swallowed silently,
        // a broken bundle looks exactly like "the image was already small enough".
        console.warn('[media] WebP WASM encoder unavailable, falling back to JPEG:', error);
        return null;
    }
}
/**
 * True when every pixel is fully opaque — meaning JPEG is a safe fallback (it
 * would otherwise flatten transparency onto black). Used for the Safari path,
 * where WebP encoding isn't available. Bails to `false` on any error.
 */
function isOpaque(ctx, width, height) {
    try {
        const { data } = ctx.getImageData(0, 0, width, height);
        for (let i = 3; i < data.length; i += 4) {
            if (data[i] !== 255)
                return false;
        }
        return true;
    }
    catch {
        return false;
    }
}
/** Draws `bitmap` at `targetW`×`targetH` and encodes it, honouring the format. */
async function renderAndEncode(bitmap, targetW, targetH, sourceType) {
    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx)
        return null;
    /* Por defecto el lienzo reduce con calidad «low»: de 4000 a 400 px salen
       dientes de sierra y bordes blandos. «high» usa un filtro de verdad. */
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    // 1) Native WebP (Chrome, modern browsers — fast, no WASM).
    // 2) WASM WebP for browsers without native encoding (Safari) — keeps transparency.
    // 3) JPEG as a last resort, but only for opaque images (JPEG has no alpha).
    const quality = qualityFor(sourceType, Math.max(targetW, targetH));
    let blob = await encode(canvas, 'image/webp', quality);
    if (!blob)
        blob = await encodeWebpWasm(ctx, targetW, targetH, quality);
    if (!blob) {
        const canJpeg = sourceType === 'image/jpeg' || sourceType === 'image/bmp' || isOpaque(ctx, targetW, targetH);
        if (canJpeg)
            blob = await encode(canvas, 'image/jpeg', Math.min(0.95, quality + JPEG_QUALITY_BONUS));
    }
    return blob;
}
/**
 * Las copias que merecen la pena para un máster de `masterWidth` píxeles.
 *
 * Se mide contra el MÁSTER, no contra el original: una foto vertical de
 * 3000×4500 se queda en 1333×2000, y medir contra los 3000 del original hacía
 * una copia de 1280 casi igual al máster —encodarla, subirla y guardarla para
 * nada—. Por lo mismo se descarta la que se queda a menos de un 15 % del
 * máster. Pura para poder probarla: la codificación de alrededor necesita un
 * lienzo, que el entorno de pruebas no tiene.
 */
export function variantWidthsFor(masterWidth) {
    return IMAGE_VARIANT_WIDTHS.filter((width) => width < masterWidth * 0.85);
}
const noVariants = () => Promise.resolve([]);
/**
 * Prepara una imagen para subirla: el máster y sus copias estrechas.
 *
 * Una sola decodificación para todo. Antes se decodificaba dos veces —una para
 * el máster y otra, desde cero, para las copias—, y con una foto de móvil de
 * 12 MP eso es lo más caro de todo el proceso después de la red. Las copias
 * salen del ORIGINAL decodificado, no del máster ya comprimido: comprimir dos
 * veces pierde detalle.
 *
 * Nada de esto lanza: si el navegador no puede decodificar o codificar, sube
 * el original sin copias.
 */
export async function prepareImageUpload(file, options = {}) {
    if (!COMPRESSIBLE.has(file.type))
        return { file, variants: noVariants() };
    let bitmap;
    try {
        bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    }
    catch {
        return { file, variants: noVariants() };
    }
    const { width, height } = bitmap;
    if (!width || !height) {
        bitmap.close?.();
        return { file, variants: noVariants() };
    }
    const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
    const masterW = Math.max(1, Math.round(width * scale));
    const masterH = Math.max(1, Math.round(height * scale));
    let master = file;
    try {
        const blob = await renderAndEncode(bitmap, masterW, masterH, file.type);
        if (blob && blob.size < file.size) {
            const ext = EXT_FOR[blob.type] ?? 'webp';
            master = new File([blob], file.name.replace(/\.[^.]+$/, '') + '.' + ext, { type: blob.type, lastModified: Date.now() });
        }
        else {
            // Dice cuál de los dos pasó. Recomprimir un JPEG ya comprimido, a su
            // tamaño, rara vez gana — así que ahí es lo esperado y en otro caso un fallo.
            console.info(blob
                ? `[media] Kept the original: re-encode was ${Math.round(blob.size / 1024)} KB vs ${Math.round(file.size / 1024)} KB.`
                : '[media] Kept the original: no encoder produced a usable image.');
        }
    }
    catch {
        master = file;
    }
    if (options.variants === false) {
        bitmap.close?.();
        return { file: master, variants: noVariants() };
    }
    const variants = (async () => {
        const out = [];
        try {
            const base = file.name.replace(/\.[^.]+$/, '');
            for (const target of variantWidthsFor(masterW)) {
                const targetH = Math.max(1, Math.round((height / width) * target));
                const blob = await renderAndEncode(bitmap, target, targetH, file.type);
                // Una copia que pesa lo mismo que el máster no ahorra nada.
                if (!blob || blob.size >= master.size)
                    continue;
                const ext = EXT_FOR[blob.type] ?? 'webp';
                out.push({ width: target, file: new File([blob], `${base}-${target}.${ext}`, { type: blob.type, lastModified: Date.now() }) });
            }
        }
        catch {
            /* Las que salieron, salieron. */
        }
        finally {
            bitmap.close?.();
        }
        return out;
    })();
    return { file: master, variants };
}
/** Sólo el máster, para lo que no lleva copias (el logo del espacio, la biblioteca). */
export async function compressImageForUpload(file) {
    return (await prepareImageUpload(file, { variants: false })).file;
}
//# sourceMappingURL=imageCompress.js.map